import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, Sheet, CircularProgress, Alert, LinearProgress, Chip, Grid } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';
import type { SystemStats } from '../../api/admin.ts';
import { PageHeader, SectionCard } from '../../components/admin/Page.tsx';

function formatBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDuration(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
}

function StatCard({ label, value, hint, color = 'primary' }: StatCardProps) {
  return (
    <Sheet
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 'lg',
        boxShadow: 'sm',
        bgcolor: 'background.surface',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        borderLeft: '4px solid',
        borderLeftColor: `${color}.500`
      }}
    >
      <Typography level="body-xs" color="neutral" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography level="h2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
      {hint && <Typography level="body-xs" color="neutral">{hint}</Typography>}
    </Sheet>
  );
}

export default function Stats() {
  const { api } = useAdmin();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    api.getStats()
      .then(s => { setStats(s); setError(''); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert color="danger">{error}</Alert></Box>;
  if (!stats) return null;

  const memColor = stats.memory.usedPercent > 85 ? 'danger' : stats.memory.usedPercent > 65 ? 'warning' : 'success';
  const sortedDomains = Object.entries(stats.usersByDomain).sort((a, b) => b[1] - a[1]);
  const topCount = sortedDomains[0]?.[1] || 1;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="Statistics"
        subtitle={`Snapshot for ${stats.date} · auto-refresh every 15s`}
        actions={
          <Chip variant="soft" color={stats.debug ? 'warning' : 'success'}>
            {stats.debug ? 'DEBUG' : 'PRODUCTION'}
          </Chip>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <StatCard label="Total users" value={stats.totalUsers.toLocaleString()} hint={`${Object.keys(stats.usersByDomain).length} domains`} />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard label="Mails today" value={stats.todayMailCount.toLocaleString()} hint={`TTL ${Math.floor(stats.mailTtlSeconds / 60)} min`} color="success" />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard label="Uptime" value={formatDuration(stats.uptimeSeconds)} hint={`${stats.platform} · ${stats.arch}`} color="neutral" />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <StatCard label="HTTP port" value={stats.httpPort} hint={`Rate ${stats.rateLimit.max}/${stats.rateLimit.windowMinutes}m`} color="warning" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <SectionCard title="Memory" description={`${formatBytes(stats.memory.usedBytes)} of ${formatBytes(stats.memory.totalBytes)} used`}>
            <Stack spacing={1}>
              <LinearProgress determinate value={stats.memory.usedPercent} color={memColor} thickness={12} sx={{ borderRadius: 'sm' }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography level="body-sm" color="neutral">{stats.memory.usedPercent}% used</Typography>
                <Typography level="body-sm" color="neutral">Free: {formatBytes(stats.memory.freeBytes)}</Typography>
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid xs={12} md={6}>
          <SectionCard title="CPU" description={`${stats.cpu.cores} cores · load average`}>
            <Stack direction="row" spacing={2}>
              {stats.cpu.loadAvg.map((v, i) => {
                const pct = Math.min(100, (v / stats.cpu.cores) * 100);
                const c = pct > 85 ? 'danger' : pct > 60 ? 'warning' : 'success';
                return (
                  <Box key={i} sx={{ flex: 1 }}>
                    <Typography level="body-xs" color="neutral">{['1m', '5m', '15m'][i]}</Typography>
                    <Typography level="title-md" sx={{ fontWeight: 600 }}>{v.toFixed(2)}</Typography>
                    <LinearProgress determinate value={pct} color={c} sx={{ mt: 0.5 }} />
                  </Box>
                );
              })}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid xs={12} md={7}>
          <SectionCard title="Users by domain" description="Top domains by mailbox count">
            {sortedDomains.length === 0 ? (
              <Typography color="neutral" level="body-sm">No mailboxes yet</Typography>
            ) : (
              <Stack spacing={1.5}>
                {sortedDomains.map(([d, n]) => (
                  <Box key={d}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>{d}</Typography>
                      <Typography level="body-sm" color="neutral">{n.toLocaleString()}</Typography>
                    </Stack>
                    <LinearProgress determinate value={(n / topCount) * 100} thickness={8} sx={{ borderRadius: 'sm' }} />
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        <Grid xs={12} md={5}>
          <SectionCard title="Configuration">
            <Stack spacing={1.5}>
              <Box>
                <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>Allowed domains</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {stats.domains.length ? stats.domains.map(d => <Chip key={d} size="sm" variant="soft">{d}</Chip>) : <Typography level="body-sm" color="neutral">—</Typography>}
                </Box>
              </Box>
              <Box>
                <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>Sender allow-list</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {stats.senderAllows.length ? stats.senderAllows.map(d => <Chip key={d} size="sm" variant="soft" color="success">{d}</Chip>) : <Chip size="sm" color="danger" variant="soft">All blocked</Chip>}
                </Box>
              </Box>
              <Box>
                <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>Rate limit</Typography>
                <Typography level="body-sm">{stats.rateLimit.max} attempts / {stats.rateLimit.windowMinutes} min</Typography>
              </Box>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
